import {
  Action,
  IAction,
  ItemValue,
  QuestionMatrixDropdownModel,
  createDropdownActionModel,
  surveyLocalization,
} from "survey-core";
import type {
  SurveyCreatorModel,
  Translation,
  TranslationItem,
} from "survey-creator-core";

const ROW_TRANSLATE_ACTION_ID = "rubi-translate-row";
const ROW_ACTIONS_CELL_CLASS = "rubi-st-row-actions";
const TRANSLATION_GRID_STYLE_ID = "rubi-translation-grid-styles";
const ALL_LANGUAGES_ITEM_ID = "rubi-translate-all-languages";

// The grid is two surveys, the column header and the strings, and every
// string is its own one-row table. Fixed column widths keep all of those
// tables lined up; once the columns outgrow the tab, the wrapper scrolls both
// surveys together (the header sticks to the top). The label column and the
// translate button stick to the left edge. The actions cell has a negative
// margin as wide as itself, so it takes no room in the row and the language
// columns stay under their headers.
const translationGridStyles = `
  .svc-translation-tab {
    --rubi-st-label-width: 240px;
    --rubi-st-column-width: 200px;
    /* SurveyJS's icon button: a 16px icon with 8px padding on each side. */
    --rubi-st-action-width: 32px;
  }
  .svc-translation-tab .st-strings-wrapper {
    overflow: auto;
  }
  .svc-translation-tab .st-strings-header {
    position: sticky;
    top: 0;
    z-index: 3;
  }
  .svc-translation-tab .st-strings,
  .svc-translation-tab .st-root-modern {
    overflow: visible;
    max-height: none;
  }
  .svc-translation-tab .st-body,
  .svc-translation-tab .st-table {
    width: max-content;
    min-width: 100%;
  }
  .svc-translation-tab .sd-table-wrapper {
    min-width: 100%;
  }
  .svc-translation-tab .st-table__cell {
    flex: 1 0 auto;
    width: var(--rubi-st-column-width);
  }
  .svc-translation-tab .st-table__cell:not(:first-child) {
    padding-left: 8px;
  }
  .svc-translation-tab td.st-table__cell:first-of-type {
    flex: none;
    order: -2;
    width: var(--rubi-st-label-width);
    padding-right: calc(var(--rubi-st-action-width) + 4px);
    position: sticky;
    left: 0;
    z-index: 1;
    overflow-wrap: anywhere;
    box-shadow: 1px 0 0 var(--ctr-string-table-row-border-color, var(--sjs-border-10, gainsboro));
  }
  .svc-translation-tab .st-panel__title > .sv-string-viewer {
    display: inline-block;
    position: sticky;
    left: 24px;
  }
  .svc-translation-tab .${ROW_ACTIONS_CELL_CLASS} {
    flex: none;
    order: -1;
    width: var(--rubi-st-action-width);
    margin-left: calc(-1 * var(--rubi-st-action-width));
    padding: 4px 0 0;
    position: sticky;
    left: calc(var(--rubi-st-label-width) - var(--rubi-st-action-width));
    z-index: 2;
  }
  /* Its menu renders inside the cell; lift it above later rows and the header. */
  .svc-translation-tab .${ROW_ACTIONS_CELL_CLASS}:has(.sd-action--pressed) {
    z-index: 4;
  }
`;

function injectTranslationGridStyles() {
  if (typeof document === "undefined") return;

  let styleElement = document.getElementById(TRANSLATION_GRID_STYLE_ID);
  if (!styleElement) {
    styleElement = document.createElement("style");
    styleElement.id = TRANSLATION_GRID_STYLE_ID;
    document.head.appendChild(styleElement);
  }
  styleElement.textContent = translationGridStyles;
}

// Routes through the creator's onMachineTranslate handler so locale mapping
// and the API call live in one place (FormBuilder.tsx).
function translateText(
  creator: SurveyCreatorModel,
  text: string,
  locale: string,
): Promise<string | undefined> {
  return new Promise((resolve) => {
    creator.doMachineTranslation(
      surveyLocalization.defaultLocale,
      locale,
      [text],
      (translated) => resolve(translated?.[0]),
    );
  });
}

/**
 * Machine-translates one translation-grid string into `locales`, overwriting
 * any existing text in those locales.
 */
export async function translateItem(
  creator: SurveyCreatorModel,
  translation: Translation,
  item: TranslationItem,
  locales: string[],
) {
  // `true` skips placeholders (e.g. a question name shown in place of an empty
  // title) so we never send them off for translation.
  const source = item.getDefaultLocaleText(true);
  if (!source) {
    creator.notify("Nothing to translate: the default text is empty");
    return;
  }

  const results = await Promise.all(
    locales.map((locale) => translateText(creator, source, locale)),
  );

  const failedLocales: string[] = [];
  results.forEach((text, index) => {
    if (text) {
      item.setLocText(locales[index], text);
    } else {
      failedLocales.push(translation.getLocaleName(locales[index]));
    }
  });
  translation.updateStringsSurveyData();

  if (failedLocales.length > 0) {
    creator.notify(
      `Couldn't translate to ${failedLocales.join(", ")}`,
      "error",
    );
  }
}

function createRowTranslateAction(
  creator: SurveyCreatorModel,
  translation: Translation,
  item: TranslationItem,
): Action | undefined {
  // The default locale is the source column; translating into it would
  // overwrite the source text.
  const locales = translation
    .getSelectedLocales()
    .filter((locale) => locale !== surveyLocalization.defaultLocale);
  if (locales.length === 0) return undefined;

  const run = async (action: Action, targetLocales: string[]) => {
    action.enabled = false;
    try {
      await translateItem(creator, translation, item, targetLocales);
    } finally {
      action.enabled = true;
    }
  };

  const actionOptions: IAction = {
    id: ROW_TRANSLATE_ACTION_ID,
    iconName: "icon-language",
    iconSize: 16,
    location: "end",
    showTitle: false,
    title: "Translate",
  };

  if (locales.length === 1) {
    const action: Action = new Action({
      ...actionOptions,
      tooltip: `Translate to ${translation.getLocaleName(locales[0])}`,
      action: () => run(action, locales),
    });
    return action;
  }

  const action = createDropdownActionModel(
    { ...actionOptions, tooltip: "Translate" },
    {
      // Open over the language columns. The default ("left") opens over
      // whatever sits left of the grid, like the portal's side nav, and only
      // flips when the menu would leave the window.
      horizontalPosition: "right",
      items: [
        { id: ALL_LANGUAGES_ITEM_ID, title: "All languages" },
        ...locales.map((locale) => ({
          id: locale,
          title: translation.getLocaleName(locale),
        })),
      ],
      onSelectionChanged: (selected) =>
        run(
          action,
          locales.filter(
            (locale) =>
              selected.id === ALL_LANGUAGES_ITEM_ID || locale === selected.id,
          ),
        ),
    },
  );
  return action;
}

/**
 * Adds a translate action to every string row of the Translations tab grid.
 * With one target language column it translates the row directly; with more
 * it opens a menu of "All languages" plus each language (a single cell).
 */
export function registerRowTranslateAction(creator: SurveyCreatorModel) {
  injectTranslationGridStyles();

  creator.onSurveyInstanceCreated.add((_, options) => {
    if (options.area !== "translation-tab:table") return;

    const translation = options.model as Translation;
    const survey = options.survey;

    // Fires again whenever the locale columns change, so the action always
    // reflects the languages currently shown.
    survey.onGetMatrixRowActions.add((_, rowOptions) => {
      const matrix = rowOptions.question as QuestionMatrixDropdownModel;
      const itemValue = ItemValue.getItemByValue(
        matrix.rows,
        rowOptions.row.rowName,
      );
      const item: TranslationItem | undefined = itemValue?.["translationData"];
      if (!item) return;

      const action = createRowTranslateAction(creator, translation, item);
      if (action) rowOptions.actions.push(action);
    });

    survey.onUpdateQuestionCssClasses.add((_, cssOptions) => {
      if (cssOptions.question.getType() !== "matrixdropdown") return;
      cssOptions.cssClasses.actionsCell = ROW_ACTIONS_CELL_CLASS;
    });
  });
}
